import { useState, useEffect } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { Loader2, AlertCircle } from 'lucide-react'
import { filesApi } from '@papan/files/client'
import { UniverModifier } from './UniverModifier'
import { errorText } from '@papan/api-client'

interface XlsxViewerProps {
  fileId: string
  fileName?: string
  versionId: string
}

/**
 * XLSX version preview — fetches Y.Doc state from server preview endpoint,
 * then renders UniverModifier in read-only preview mode.
 */
export function XlsxViewer({ fileId, fileName, versionId }: XlsxViewerProps) {
  const [previewData, setPreviewData] = useState<ArrayBuffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // #1316: a dropped answer (its session ended) is asked again as the session now here — never shown as an error
  // Review round 2: in the share view an end that MOVES the share session leaves the ended sid in use until the new
  // session lands — the re-ask waits for it there (`useDroppedLoadReask`), never asking as the ended session
  const { reask, begin } = useDroppedLoadReask()

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    setLoading(true)
    setError(null)
    setPreviewData(null)

    filesApi.getPreview(fileId, versionId)
      .then((data: any) => {
        if (data.type === 'ydoc' && data.state) {
          // Decode base64 Y.Doc state
          const binary = Uint8Array.from(atob(data.state), c => c.charCodeAt(0))
          setPreviewData(binary.buffer)
        } else {
          setError('Preview not available for this file type')
        }
        setLoading(false)
      })
      .catch(err => {
        const text = errorText(err, 'Failed to load preview')
        if (text === null) { asked.dropped(); return }
        setError(text)
        setLoading(false)
      })
  }, [fileId, versionId, reask, begin])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error || !previewData) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">{error || 'No preview data'}</p>
        </div>
      </div>
    )
  }

  return (
    <UniverModifier
      room=""
      fileName={fileName}
      mode="readonly"
      previewData={previewData}
    />
  )
}
